import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../../../db/firebase";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import AddSongModal from "../Songs/AddSongModal";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { Toast } from "../../../components/Toast";
import CifraImportPanel from "../../../components/CifraImportPanel";
import { CifraImportPrefill } from "../../../utils/cifraApi";
import {
  HiOutlineXMark,
  HiOutlineMagnifyingGlass,
  HiOutlineMusicalNote,
  HiOutlineChevronDown,
  HiOutlineCheck,
  HiOutlinePlus,
  HiOutlineHeart,
  HiOutlineGlobeAlt,
} from "react-icons/hi2";
import { useTranslation } from "react-i18next";

const SongArtworkBlock: React.FC<{
  artworkURL?: string | null;
  title: string;
}> = ({ artworkURL, title }) => {
  const [imgError, setImgError] = React.useState(false);
  if (!artworkURL || imgError) {
    return (
      <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 shrink-0">
        <HiOutlineMusicalNote className="w-6 h-6" strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <img
      src={artworkURL}
      alt={title}
      onError={() => setImgError(true)}
      className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover shrink-0"
    />
  );
};

interface SearchAddSongModalProps {
  eventId: string;
  currentSongs: any[];
  onClose: () => void;
}

const SearchAddSongModal: React.FC<SearchAddSongModalProps> = ({
  eventId,
  currentSongs,
  onClose,
}) => {
  const { t } = useTranslation();
  const [songSearch, setSongSearch] = useState("");
  const [favoriteSongs, setFavoriteSongs] = useState<any[]>([]);
  const [allSongs, setAllSongs] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [addSongPrefill, setAddSongPrefill] = useState<CifraImportPrefill | undefined>();
  const [importNotice, setImportNotice] = useState("");
  const [importAutoSearch, setImportAutoSearch] = useState(false);
  const [loadingSongs, setLoadingSongs] = useState(true);
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false);
  const [artistSearchQuery, setArtistSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "favorites" | "import">(
    "all",
  );
  const artistDropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [processingSongId, setProcessingSongId] = useState<string | null>(null);
  const [justAddedId, setJustAddedId] = useState<string | null>(null);
  const [justRemovedId, setJustRemovedId] = useState<string | null>(null);
  const [songToRemove, setSongToRemove] = useState<any | null>(null);
  const [showRemovedToast, setShowRemovedToast] = useState(false);
  const [showAddedToast, setShowAddedToast] = useState(false);
  const [localEventSongIds, setLocalEventSongIds] = useState<Set<string>>(
    () => new Set(currentSongs.map((s) => s.id)),
  );
  const itemsPerPage = 10;

  useEffect(() => {
    setLocalEventSongIds(new Set(currentSongs.map((s) => s.id)));
  }, [currentSongs]);

  const allArtists = useMemo(() => {
    const artists = new Set<string>();
    allSongs.forEach((s) => {
      const artist = (s.artist || "").trim();
      if (artist) artists.add(artist);
    });
    return Array.from(artists).sort((a, b) => a.localeCompare(b));
  }, [allSongs]);

  const filteredArtists = useMemo(() => {
    const q = artistSearchQuery.trim().toLowerCase();
    if (!q) return allArtists;
    return allArtists.filter((a) => a.toLowerCase().includes(q));
  }, [allArtists, artistSearchQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        artistDropdownRef.current &&
        !artistDropdownRef.current.contains(e.target as Node)
      ) {
        setArtistDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadSongs = useCallback(async () => {
    setLoadingSongs(true);
    const user = auth.currentUser;
    try {
      const allSongsSnapshot = await getDocs(collection(db, "songs"));
      const songsData = allSongsSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setAllSongs(songsData);

      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const orgId = userDoc.data().organizationId;
          if (orgId) {
            const favSongsRef = collection(
              db,
              "organizations",
              orgId,
              "favorite_songs",
            );
            const favSongsSnapshot = await getDocs(favSongsRef);
            const favSongIds = favSongsSnapshot.docs.map((d) => d.id);
            setFavoriteSongs(
              favSongIds.length > 0
                ? songsData.filter((s) => favSongIds.includes(s.id))
                : [],
            );
          }
        }
      }
    } catch (error) {
      console.error("Error loading songs:", error);
    } finally {
      setLoadingSongs(false);
    }
  }, []);

  useEffect(() => {
    loadSongs();
  }, [loadSongs]);

  const displayedSongs = useMemo(() => {
    const source = activeTab === "favorites" ? favoriteSongs : allSongs;
    const q = songSearch.trim().toLowerCase();
    return source.filter((s: any) => {
      if (selectedArtist && (s.artist || "").trim() !== selectedArtist) {
        return false;
      }
      if (!q) return true;
      const title = (s.title || "").toLowerCase();
      const artist = (s.artist || "").toLowerCase();
      const album = (s.album || "").toLowerCase();
      return title.includes(q) || artist.includes(q) || album.includes(q);
    });
  }, [activeTab, favoriteSongs, allSongs, songSearch, selectedArtist]);

  const totalPages = Math.max(1, Math.ceil(displayedSongs.length / itemsPerPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedArtist, songSearch, activeTab]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [currentPage]);

  const handleClear = () => {
    setSongSearch("");
    setSelectedArtist(null);
    setCurrentPage(1);
  };

  const addSongIdToEvent = async (songId: string) => {
    const eventDocRef = doc(db, "events", eventId);
    const eventDoc = await getDoc(eventDocRef);
    if (!eventDoc.exists()) return;
    const eventData = eventDoc.data();
    const existing: string[] = eventData?.songs || [];
    if (existing.includes(songId)) {
      setLocalEventSongIds((prev) => new Set(prev).add(songId));
      return;
    }
    await updateDoc(eventDocRef, { songs: [...existing, songId] });
    setLocalEventSongIds((prev) => new Set(prev).add(songId));
  };

  const handleSelectSong = async (song: any) => {
    setProcessingSongId(song.id);
    try {
      await addSongIdToEvent(song.id);
      setJustAddedId(song.id);
      setTimeout(() => setJustAddedId(null), 800);
    } catch (error) {
      console.error("Error adding song to event:", error);
    } finally {
      setProcessingSongId(null);
    }
  };

  const openManualAdd = (prefill?: CifraImportPrefill, notice?: string) => {
    setAddSongPrefill(
      prefill || (songSearch.trim() ? { title: songSearch.trim() } : undefined),
    );
    setImportNotice(notice || "");
    setShowAddSongModal(true);
  };

  const openImporterWithQuery = () => {
    setActiveTab("import");
    setImportAutoSearch(Boolean(songSearch.trim()));
  };

  const handleImportedSongSaved = async (songId?: string) => {
    await loadSongs();
    setActiveTab("all");
    setImportAutoSearch(false);
    if (songId) {
      try {
        await addSongIdToEvent(songId);
        setJustAddedId(songId);
        setTimeout(() => setJustAddedId(null), 800);
        setShowAddedToast(true);
      } catch (error) {
        console.error("Error adding imported song to event:", error);
      }
    }
  };

  const handleRequestRemoveSong = (song: any) => {
    setSongToRemove(song);
  };

  const handleConfirmRemoveSong = async () => {
    if (!songToRemove) return;
    const songId = songToRemove.id;
    setProcessingSongId(songId);
    try {
      const eventDocRef = doc(db, "events", eventId);
      const eventDoc = await getDoc(eventDocRef);
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        const updatedSongs = (eventData?.songs || []).filter(
          (id: string) => id !== songId,
        );
        await updateDoc(eventDocRef, { songs: updatedSongs });
        setLocalEventSongIds((prev) => {
          const next = new Set(prev);
          next.delete(songId);
          return next;
        });
        setSongToRemove(null);
        setJustRemovedId(songId);
        setTimeout(() => setJustRemovedId(null), 800);
        setShowRemovedToast(true);
      }
    } catch (error) {
      console.error("Error removing song from event:", error);
    } finally {
      setProcessingSongId(null);
    }
  };

  const pageSongs = displayedSongs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const hasFilters = Boolean(songSearch.trim() || selectedArtist);

  return (
    <>
      <AnimatedModal
        isOpen
        onClose={onClose}
        contentClassName="sm:max-w-3xl lg:max-w-4xl"
      >
        <div className={modalPanelClass}>
          <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shrink-0">
                <HiOutlineMusicalNote className="w-5 h-5" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 tracking-tight truncate">
                  {t("searchAddSongs")}
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                  {t("searchAddSongsSubtitle")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
              aria-label={t("close")}
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>

          {/* Search + filters */}
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 dark:border-gray-700 space-y-3 shrink-0">
            {activeTab !== "import" && (
              <div className="flex gap-2">
                <div className="relative flex-1 min-w-0">
                  <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="search"
                    value={songSearch}
                    onChange={(e) => setSongSearch(e.target.value)}
                    placeholder={t("searchByNameOrArtist")}
                    autoFocus
                    className="w-full h-11 pl-10 pr-3 rounded-xl text-sm border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
                    maxLength={150}
                  />
                </div>

                <div className="relative shrink-0" ref={artistDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setArtistDropdownOpen((o) => !o)}
                    className={`h-11 max-w-[9.5rem] sm:max-w-[12rem] px-3 rounded-xl text-sm border inline-flex items-center gap-1.5 backdrop-blur-sm transition-colors ${
                      selectedArtist
                        ? "border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd]"
                        : "border-gray-200/80 dark:border-gray-600/80 bg-white/70 dark:bg-gray-900/60 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500"
                    }`}
                  >
                    <span className="truncate">
                      {selectedArtist || t("allArtists")}
                    </span>
                    <HiOutlineChevronDown
                      className={`w-4 h-4 shrink-0 transition-transform ${
                        artistDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {artistDropdownOpen && (
                    <div className="absolute top-full right-0 mt-1.5 w-64 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl z-50 overflow-hidden flex flex-col max-h-72">
                      <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                        <input
                          type="text"
                          value={artistSearchQuery}
                          onChange={(e) => setArtistSearchQuery(e.target.value)}
                          placeholder={`${t("search")} ${t("artist")}...`}
                          className="w-full px-3 py-2 rounded-lg text-sm border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30"
                          autoFocus
                        />
                      </div>
                      <ul className="overflow-y-auto py-1">
                        <li>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedArtist(null);
                              setArtistDropdownOpen(false);
                              setArtistSearchQuery("");
                            }}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                              !selectedArtist
                                ? "bg-[#5932EA]/10 text-[#5932EA] font-medium"
                                : "text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {t("allArtists")}
                          </button>
                        </li>
                        {filteredArtists.map((artist) => (
                          <li key={artist}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedArtist(artist);
                                setArtistDropdownOpen(false);
                                setArtistSearchQuery("");
                              }}
                              className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 truncate ${
                                selectedArtist === artist
                                  ? "bg-[#5932EA]/10 text-[#5932EA] font-medium"
                                  : "text-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {artist}
                            </button>
                          </li>
                        ))}
                        {filteredArtists.length === 0 && (
                          <li className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {t("noArtistsFound")}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {hasFilters && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="h-11 px-3 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700/80 shrink-0 transition-colors"
                  >
                    {t("clear")}
                  </button>
                )}
              </div>
            )}

            {activeTab !== "import" && (
              <div className="flex items-center justify-between gap-3">
                <div className="inline-flex p-1 rounded-xl bg-gray-100 dark:bg-gray-900/80 overflow-x-auto max-w-full">
                  <button
                    type="button"
                    onClick={() => setActiveTab("all")}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === "all"
                        ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    }`}
                  >
                    {t("allSongs")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("favorites")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === "favorites"
                        ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    }`}
                  >
                    <HiOutlineHeart className="w-4 h-4" />
                    {t("favoriteSongs")}
                    {favoriteSongs.length > 0 && (
                      <span className="text-xs opacity-70">
                        ({favoriteSongs.length})
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab("import");
                      setImportAutoSearch(Boolean(songSearch.trim()));
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === "import"
                        ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    }`}
                  >
                    <HiOutlineGlobeAlt className="w-4 h-4" />
                    {t("songImporter") || "Import"}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => openManualAdd()}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors shrink-0"
                >
                  <HiOutlinePlus className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("addNewSong")}</span>
                  <span className="sm:hidden">{t("add")}</span>
                </button>
              </div>
            )}

            {activeTab === "import" && (
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("all");
                    setImportAutoSearch(false);
                  }}
                  className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-[#5932EA]"
                >
                  ← {t("allSongs")}
                </button>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5932EA]">
                  <HiOutlineGlobeAlt className="w-4 h-4" />
                  {t("songImporter") || "Song Importer"}
                </span>
              </div>
            )}
          </div>

          {/* Results */}
          <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
            {activeTab === "import" ? (
              <div className="px-4 sm:px-5 py-4">
                <CifraImportPanel
                  compact
                  initialQuery={songSearch}
                  autoSearch={importAutoSearch}
                  onImportReady={(prefill, notice) => {
                    openManualAdd(prefill, notice);
                  }}
                />
              </div>
            ) : loadingSongs ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <span className="w-10 h-10 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("loadingSongs")}
                </p>
              </div>
            ) : allSongs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <HiOutlineMusicalNote
                  className="w-14 h-14 text-gray-400 dark:text-gray-500 mb-3 opacity-50"
                  strokeWidth={1.5}
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {t("noSongsInLibrary")}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={openImporterWithQuery}
                    className="py-2.5 px-5 rounded-xl font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 inline-flex items-center justify-center gap-2 transition-colors"
                  >
                    <HiOutlineGlobeAlt className="w-4 h-4" />
                    {t("searchSongImporter") || "Search Song Importer"}
                  </button>
                  <button
                    type="button"
                    onClick={() => openManualAdd()}
                    className="py-2.5 px-5 rounded-xl font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700/80 transition-colors"
                  >
                    {t("addNewSong")}
                  </button>
                </div>
              </div>
            ) : pageSongs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <HiOutlineMagnifyingGlass
                  className="w-14 h-14 text-gray-400 dark:text-gray-500 mb-3 opacity-50"
                  strokeWidth={1.5}
                />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                  {activeTab === "favorites" && favoriteSongs.length === 0
                    ? t("noSongsInSearchResults")
                    : selectedArtist
                      ? t("noSongsForArtist", { artist: selectedArtist })
                      : t("noSongsMatching")}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
                  {t("trySongImporterHint") ||
                    "Not in your library? Import chords from Song Importer."}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <button
                    type="button"
                    onClick={openImporterWithQuery}
                    className="py-2.5 px-5 rounded-xl font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 inline-flex items-center justify-center gap-2 transition-colors"
                  >
                    <HiOutlineGlobeAlt className="w-4 h-4" />
                    {t("searchSongImporter") || "Search Song Importer"}
                  </button>
                  {hasFilters && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="py-2.5 px-5 rounded-xl font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700/80 transition-colors"
                    >
                      {t("clear")}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="px-3 sm:px-5 py-3">
                <p className="px-1 pb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {displayedSongs.length} {t("songs").toLowerCase()}
                  {selectedArtist ? ` · ${selectedArtist}` : ""}
                </p>
                <ul className="space-y-1.5">
                  {pageSongs.map((song) => {
                    const isInEvent = localEventSongIds.has(song.id);
                    const isProcessing = processingSongId === song.id;
                    const showAdded = justAddedId === song.id;
                    const showRemoved = justRemovedId === song.id;

                    return (
                      <li
                        key={song.id}
                        className={`flex items-center gap-3 p-2.5 sm:p-3 rounded-xl border backdrop-blur-sm transition-colors ${
                          isInEvent
                            ? "bg-emerald-50/60 dark:bg-emerald-900/15 border-emerald-200/60 dark:border-emerald-800/40"
                            : "bg-white/50 dark:bg-gray-900/30 border-gray-200/70 dark:border-gray-700/70 hover:border-[#5932EA]/25 dark:hover:border-[#5932EA]/30"
                        }`}
                      >
                        <SongArtworkBlock
                          artworkURL={song.artworkURL}
                          title={song.title}
                        />
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm sm:text-base truncate">
                            {song.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                            {song.artist || "—"}
                            {song.album ? (
                              <span className="text-gray-400 dark:text-gray-500">
                                {" "}
                                · {song.album}
                              </span>
                            ) : null}
                          </p>
                        </div>
                        {isInEvent ? (
                          <button
                            type="button"
                            onClick={() =>
                              !isProcessing && handleRequestRemoveSong(song)
                            }
                            disabled={isProcessing}
                            className={`shrink-0 min-w-[4.5rem] h-9 px-3 rounded-xl text-sm font-medium backdrop-blur-sm transition-colors disabled:opacity-70 ${
                              showRemoved
                                ? "border border-gray-200 dark:border-gray-600 bg-gray-100/80 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300"
                                : "border border-red-300/45 dark:border-red-500/30 bg-red-50/70 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100/80 dark:hover:bg-red-900/35"
                            }`}
                          >
                            {isProcessing ? (
                              <span className="w-4 h-4 border-2 border-red-400/30 border-t-red-500 rounded-full animate-spin inline-block" />
                            ) : showRemoved ? (
                              <span className="inline-flex items-center gap-1">
                                <HiOutlineCheck className="w-4 h-4" />
                                {t("removed")}
                              </span>
                            ) : (
                              t("remove")
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              !isProcessing && handleSelectSong(song)
                            }
                            disabled={isProcessing}
                            className={`shrink-0 min-w-[4.5rem] h-9 px-3 rounded-xl text-sm font-medium backdrop-blur-sm transition-colors disabled:opacity-70 ${
                              showAdded
                                ? "border border-emerald-300/50 dark:border-emerald-500/30 bg-emerald-50/80 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300"
                                : "border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25"
                            }`}
                          >
                            {isProcessing ? (
                              <span className="w-4 h-4 border-2 border-[#5932EA]/25 border-t-[#5932EA] rounded-full animate-spin inline-block" />
                            ) : showAdded ? (
                              <span className="inline-flex items-center gap-1">
                                <HiOutlineCheck className="w-4 h-4" />
                                {t("added")}
                              </span>
                            ) : (
                              t("add")
                            )}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* Footer pagination */}
          {activeTab !== "import" &&
            !loadingSongs &&
            displayedSongs.length > 0 && (
              <div className="px-4 sm:px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-10 px-4 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/50 dark:bg-gray-800/40 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700/70 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  {t("previous")}
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                  {t("pageOf", { current: currentPage, total: totalPages })}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="h-10 px-4 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/50 dark:bg-gray-800/40 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700/70 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  {t("next")}
                </button>
              </div>
            )}
        </div>
      </AnimatedModal>

      {showAddSongModal && (
        <AddSongModal
          importNotice={importNotice || undefined}
          onClose={() => {
            setShowAddSongModal(false);
            setAddSongPrefill(undefined);
            setImportNotice("");
          }}
          onSongAdded={handleImportedSongSaved}
          prefillData={addSongPrefill}
        />
      )}
      <ConfirmationModal
        isOpen={!!songToRemove}
        onClose={() => setSongToRemove(null)}
        onConfirm={handleConfirmRemoveSong}
        message={t("confirmRemoveSongFromEvent")}
        loading={processingSongId === songToRemove?.id}
      />
      <Toast
        message={t("songRemovedFromEvent")}
        isVisible={showRemovedToast}
        onDismiss={() => setShowRemovedToast(false)}
      />
      <Toast
        message={
          t("importedSongAddedToEvent") ||
          "Song imported and added to this event"
        }
        isVisible={showAddedToast}
        onDismiss={() => setShowAddedToast(false)}
      />
    </>
  );
};

export default SearchAddSongModal;
